import { Link } from 'react-router-dom';
import { ArrowRight, Check, MapPin, Scissors, Dumbbell, UtensilsCrossed, HardHat } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CookieConsent from '@/components/CookieConsent';
import BackToTop from '@/components/BackToTop';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import { usePricing } from '@/context/PricingContext';

const RESULTS = [
  'Get more enquiries from local customers',
  'Turn website visitors into paying clients',
  'Rank higher on Google for Bournemouth searches',
  'Load fast on every device — mobile first',
  'Stand out from competitors in the BCP area',
  'Own a website that works as hard as you do',
];

const AREAS = ['Bournemouth Beach', 'Town Centre', 'Winton', 'Boscombe', 'Charminster', 'Westbourne', 'Southbourne', 'Lansdowne'];

const NICHES = [
  { name: 'Web Design for Barbers', href: '/web-design-barbers-bournemouth', icon: Scissors },
  { name: 'Web Design for Gyms', href: '/web-design-gyms-bournemouth', icon: Dumbbell },
  { name: 'Web Design for Restaurants', href: '/web-design-restaurants-bournemouth', icon: UtensilsCrossed },
  { name: 'Web Design for Trades', href: '/web-design-trades-bournemouth', icon: HardHat },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "additionalType": "https://schema.org/ProfessionalService",
  "name": "UI Designs — Web Design Bournemouth",
  "description": "Affordable, conversion-focused web design for small businesses in Bournemouth, Poole, Christchurch and across Dorset.",
  "url": "https://uidesigns.co.uk/web-design-bournemouth",
  "email": "info@uidesigns.co.uk",
  "priceRange": "£399 - £3,800+",
  "currenciesAccepted": "GBP",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Bournemouth",
    "addressRegion": "Dorset",
    "addressCountry": "GB"
  },
  "areaServed": [
    { "@type": "City", "name": "Bournemouth" },
    { "@type": "City", "name": "Poole" },
    { "@type": "City", "name": "Christchurch" },
    { "@type": "AdministrativeArea", "name": "Dorset" }
  ],
  "serviceType": ["Web Design", "SEO", "E-Commerce", "Landing Pages", "Branding"],
  "knowsAbout": ["Web Design Bournemouth", "SEO Bournemouth", "Small Business Websites"]
};

export default function WebDesignBournemouth() {
  const pricing = usePricing();
  const llPrice = pricing.local_launch?.price || '399';

  const schema = { ...SCHEMA, priceRange: `£${llPrice} - £3,800+` };

  const scrollToContact = () => {
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
      <SEOHead
        title="Web Design Bournemouth | Affordable Websites for Local Businesses | UI Designs"
        description={`Professional web design in Bournemouth for small businesses. Get more leads, rank higher on Google, and grow your customer base. Websites from £${llPrice}. Free consultation.`}
        canonical="https://uidesigns.co.uk/web-design-bournemouth"
        keywords="web design Bournemouth, website designer Bournemouth, affordable web design Bournemouth, SEO Bournemouth, small business websites Bournemouth, Bournemouth web developer"
        schema={schema}
      />
      <Navigation />

      <main>
        {/* Hero */}
        <section data-testid="bournemouth-hero" className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#F8F9FA] to-white dark:from-[#0A0A0A] dark:to-[#0A0A0A] pointer-events-none" />
          <div className="absolute top-10 right-[10%] w-[500px] h-[500px] bg-[#00E5FF]/8 rounded-full blur-[140px] pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <ScrollReveal>
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-8">
                  <MapPin className="w-3 h-3 text-[#00E5FF]" />
                  Bournemouth, Dorset
                </span>
              </ScrollReveal>
              <ScrollReveal delay={100}>
                <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.08] text-[#0A0A0A] dark:text-white mb-6">
                  Web Design Bournemouth
                  <br />
                  <span className="text-gradient-shimmer">That Gets Results</span>
                </h1>
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <p className="text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-400 mb-8 max-w-xl">
                  Affordable, professional web design for Bournemouth businesses. Whether you run a shop on the high street, a gym near Boscombe, or a cafe by Bournemouth Beach — I build websites that bring in customers.
                </p>
              </ScrollReveal>
              <ScrollReveal delay={300}>
                <div className="flex flex-wrap gap-4">
                  <button
                    data-testid="bournemouth-cta"
                    onClick={scrollToContact}
                    className="group inline-flex items-center gap-2 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-8 py-4 rounded-full font-medium transition-all hover:bg-[#00E5FF] hover:text-black dark:hover:bg-violet-400 pulse-glow hover:scale-[1.02]"
                  >
                    Get Your Free Quote
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <Link
                    to="/#pricing"
                    className="group inline-flex items-center gap-2 bg-white dark:bg-white/5 text-black dark:text-white border border-slate-200 dark:border-white/10 px-8 py-4 rounded-full font-medium transition-all hover:border-[#0A0A0A] dark:hover:border-[#00E5FF]"
                  >
                    See Pricing
                  </Link>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Result-Focused Bullets */}
        <section data-testid="bournemouth-results" className="py-20 md:py-28 bg-[#F8F9FA] dark:bg-[#0D0D0D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <ScrollReveal>
                  <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-6">
                    A Bournemouth Website That Actually <span className="text-[#00E5FF]">Works</span>
                  </h2>
                  <p className="text-base leading-relaxed text-slate-600 dark:text-slate-400 mb-8">
                    Most web designers sell you a pretty page. I build websites that generate real business. Every Bournemouth site I create is designed to convert visitors into enquiries and customers.
                  </p>
                </ScrollReveal>
                <ScrollReveal delay={100}>
                  <ul className="space-y-4">
                    {RESULTS.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#00E5FF]" strokeWidth={3} />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </ScrollReveal>
              </div>
              <ScrollReveal delay={200}>
                <div className="bg-white dark:bg-[#111111] rounded-2xl border border-slate-200 dark:border-white/10 p-8 md:p-10">
                  <h3 className="font-['Outfit'] text-xl font-bold text-[#0A0A0A] dark:text-white mb-4">
                    Websites from £{llPrice}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
                    Get online fast with a Local Launch single-page website. Transparent pricing with no hidden fees. Every package includes mobile-responsive design, contact form, and click-to-call — upgrade to a full site anytime.
                  </p>
                  <button
                    onClick={scrollToContact}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-6 py-3.5 rounded-full text-sm font-medium transition-all hover:bg-[#00E5FF] hover:text-black dark:hover:bg-violet-400"
                  >
                    Start Your Project <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Local Areas */}
        <section data-testid="bournemouth-areas" className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-4">
                Web Design Across Bournemouth
              </h2>
              <p className="text-base leading-relaxed text-slate-600 dark:text-slate-400 mb-10 max-w-2xl">
                Helping businesses near Bournemouth Beach and Winton grow online. From independent shops in Boscombe to professional services in Westbourne — I design websites that reflect your local reputation.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <div className="flex flex-wrap gap-3">
                {AREAS.map((area) => (
                  <span
                    key={area}
                    className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Local Niches */}
        <section data-testid="bournemouth-niches" className="py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
                Specialist Web Design for Bournemouth Businesses
              </h2>
              <p className="text-base leading-relaxed text-slate-400 mb-12 max-w-2xl">
                I work with specific industries across Bournemouth and know exactly what your customers expect to see online.
              </p>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {NICHES.map((niche, i) => (
                <ScrollReveal key={niche.name} delay={i * 100}>
                  <Link
                    to={niche.href}
                    data-testid={`niche-link-${i}`}
                    className="group block bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-[#00E5FF]/30 transition-all"
                  >
                    <niche.icon className="w-6 h-6 text-[#00E5FF] mb-4" />
                    <h3 className="font-['Outfit'] text-lg font-semibold text-white mb-2">{niche.name}</h3>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00B4D8] group-hover:gap-2.5 transition-all">
                      View details <ArrowRight className="w-3 h-3" />
                    </span>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section data-testid="bournemouth-cta-section" className="py-20 md:py-28">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <ScrollReveal>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-4">
                Ready to Grow Your Bournemouth Business Online?
              </h2>
              <p className="text-base leading-relaxed text-slate-600 dark:text-slate-400 mb-8">
                Get a free, no-obligation quote. I will get back to you within 24 hours with ideas tailored to your business.
              </p>
              <button
                onClick={scrollToContact}
                className="group inline-flex items-center gap-2 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-8 py-4 rounded-full font-medium transition-all hover:bg-[#00E5FF] hover:text-black dark:hover:bg-violet-400 pulse-glow hover:scale-[1.02]"
              >
                Get Your Free Quote
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </ScrollReveal>
          </div>
        </section>
      </main>

      <Footer />
      <CookieConsent />
      <BackToTop />
    </div>
  );
}
